import { useSession } from 'next-auth/react';
import { getAccessToken } from '@/config/authApi.config';
import { refreshToken } from '@/services/api/auth';
import { useRef } from 'react';

interface RefreshOptions {
  forceRefresh?: boolean;
  updateFields?: Record<string, any>;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export const useRefreshToken = () => {
  const { data: session, update } = useSession();
  const hasRefreshed = useRef(false);

  const refreshUserData = async (options: RefreshOptions = {}) => {
    const { forceRefresh = false, updateFields = {}, onSuccess, onError } = options;
    
    try {
      if (!forceRefresh && hasRefreshed.current || !session) return;

      if (!session?.user) return;

      const {accessToken, refreshToken: rf} = await getAccessToken(true);
      const userData = await refreshToken();

      if (userData?.result?.user) {
        await update({
          ...session,
          user: {
            ...userData.result.user,
            ...updateFields,
            refreshToken: rf,
            user: {
              ...userData.result.user,
              refreshToken: rf
            }
          },
          accessToken: userData.result.accessToken,
          refreshToken: rf
        });
        hasRefreshed.current = true;
        onSuccess?.();
      } else if (Object.keys(updateFields).length > 0) {
        // Fallback if userData is not available but we still need to update some fields
        await update({
          ...session,
          user: {
            ...session?.user,
            ...updateFields
          }
        });
        onSuccess?.();
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      
      // If we have updateFields, still try to update them even if refresh failed
      if (Object.keys(updateFields).length > 0) {
        try {
          await update({
            ...session,
            user: {
              ...session?.user,
              ...updateFields
            }
          });
          onSuccess?.();
        } catch (updateError) {
          console.error('Failed to update session after refresh error:', updateError);
        }
      }
      
      onError?.(error);
    }
  };

  return { refreshUserData, session };
};

export default useRefreshToken; 